/**
 * ProcedureViewPopover — read-only details for a procedure surfaced
 * from the FindingsSummary row's "View" action.
 *
 * Renders as a centered modal-style card (not a popover anchor) for
 * simplicity + tablet ergonomics. All data comes from the projection's
 * `plan` object (already on the row) plus the row's token label — no
 * additional API call.
 *
 * Closes on Escape, click outside, or the explicit close button.
 */
import React, { useEffect, useRef } from 'react';

import { formatAbsoluteIST, formatRelative, humanSittingStatus } from './summaryHelpers';
import { statusFillColor } from './statusFill';


// Render one Created / Started / Completed / Paid row only when the
// underlying stamp is present. Shows the EXACT date/time (IST wall-clock);
// hover tooltip carries the relative shorthand.
function LifecycleRow({ label, iso, muted }) {
  if (!iso) return null;
  return (
    <>
      <dt className={['col-span-1 font-semibold uppercase tracking-wide', muted].join(' ')}>
        {label}
      </dt>
      <dd className="col-span-2" title={formatRelative(iso) || ''}>
        {formatAbsoluteIST(iso) || iso}
      </dd>
    </>
  );
}


function ProcedureBlock({ plan, isDark, muted }) {
  const status = String(plan.status || '').toLowerCase();
  const dotColor = statusFillColor(status);
  // Created falls back to `startedAt` for legacy projections that didn't
  // surface created_at as its own field.
  const createdIso = plan.created_at || plan.startedAt || null;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 mb-1">
        <span
          className="inline-block w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: dotColor }}
          aria-hidden="true"
        />
        <span className="text-[13px] font-semibold truncate">
          {plan.name || 'Procedure'}
        </span>
        <span className={['text-[11px]', muted].join(' ')}>
          · {humanSittingStatus(status) || '—'}
        </span>
      </div>
      <dl className="grid grid-cols-3 gap-x-3 gap-y-1 text-[12px] pl-3.5">
        <LifecycleRow label="Created" iso={createdIso} muted={muted} />
        <LifecycleRow label="Started" iso={plan.executed_at} muted={muted} />
        <LifecycleRow label="Completed" iso={plan.completed_at} muted={muted} />
        <LifecycleRow label="Paid" iso={plan.payment_cleared_at} muted={muted} />
        {plan.assignee && (
          <>
            <dt className={['col-span-1 font-semibold uppercase tracking-wide', muted].join(' ')}>
              Doctor
            </dt>
            <dd className="col-span-2">{plan.assignee}</dd>
          </>
        )}
        {plan.finalCost != null && plan.finalCost > 0 && (
          <>
            <dt className={['col-span-1 font-semibold uppercase tracking-wide', muted].join(' ')}>
              Cost
            </dt>
            <dd className="col-span-2">
              ₹{Number(plan.finalCost).toLocaleString('en-IN')}
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}

export default function ProcedureViewPopover({
  plans,
  plan,
  tokenLabel,
  onClose,
  isDark = false,
}) {
  const wrapperRef = useRef(null);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Accept a list (new) or a single plan (back-compat).
  const list = Array.isArray(plans) && plans.length > 0
    ? plans
    : (plan ? [plan] : []);
  if (list.length === 0) return null;

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div
      // z-[1300] sits above the chart dialog (z-1200) so the popover
      // overlays without losing pointer events on its own buttons.
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="procedure-view-popover-title"
      onClick={(e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
          onClose?.();
        }
      }}
    >
      <div
        ref={wrapperRef}
        className={[
          'w-full max-w-md rounded-lg shadow-xl border p-4 max-h-[80vh] overflow-y-auto',
          isDark
            ? 'bg-slate-900 border-slate-700 text-slate-100'
            : 'bg-white border-slate-300 text-slate-900',
        ].join(' ')}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <h3
              id="procedure-view-popover-title"
              className="text-sm font-semibold truncate"
            >
              {list.length > 1
                ? `${list.length} procedures on this tooth`
                : (list[0].name || 'Procedure')}
            </h3>
            {tokenLabel && (
              <p className={['text-[11px] mt-0.5', muted].join(' ')}>
                {tokenLabel}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={[
              'px-2 py-1 rounded-md border text-xs',
              isDark
                ? 'bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700'
                : 'bg-white border-slate-400 text-slate-800 hover:bg-slate-100',
            ].join(' ')}
            data-testid="procedure-view-close"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3">
          {list.map((p, i) => (
            <React.Fragment key={p.sittingId || `${p.name}-${i}`}>
              {i > 0 && (
                <hr className={isDark ? 'border-slate-700' : 'border-slate-200'} />
              )}
              <ProcedureBlock plan={p} isDark={isDark} muted={muted} />
            </React.Fragment>
          ))}
        </div>

        <p className={['text-[11px] mt-3', muted].join(' ')}>
          Tap a row in the summary to add a diagnosis or use the toolbar
          actions to manage the procedure.
        </p>
      </div>
    </div>
  );
}
